import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Home, MapPin, Phone, ArrowLeft, Flag } from 'lucide-react';
import { Button } from '../components/ui/button';

const translations = {
  ca: {
    title: '404',
    subtitle: 'Pàgina no trobada',
    message: 'Ho sentim, la pàgina que busques no existeix o ha estat moguda.',
    backHome: 'Tornar a l\'inici',
    suggestions: 'Potser t\'interessa:',
    elCamp: 'El Camp',
    reservar: 'Reservar',
    contacte: 'Contacte'
  },
  es: {
    title: '404',
    subtitle: 'Página no encontrada',
    message: 'Lo sentimos, la página que buscas no existe o ha sido movida.',
    backHome: 'Volver al inicio',
    suggestions: 'Quizás te interese:',
    elCamp: 'El Campo',
    reservar: 'Reservar',
    contacte: 'Contacto'
  },
  fr: {
    title: '404',
    subtitle: 'Page non trouvée',
    message: 'Désolé, la page que vous recherchez n\'existe pas ou a été déplacée.',
    backHome: 'Retour à l\'accueil',
    suggestions: 'Peut-être vous intéresse:',
    elCamp: 'Le Parcours',
    reservar: 'Réserver',
    contacte: 'Contact'
  },
  en: {
    title: '404',
    subtitle: 'Page not found',
    message: 'Sorry, the page you are looking for does not exist or has been moved.',
    backHome: 'Back to home',
    suggestions: 'You might be interested in:',
    elCamp: 'The Course',
    reservar: 'Book now',
    contacte: 'Contact'
  }
};

const NotFoundPage = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.ca;

  return (
    <main className="min-h-screen bg-[#F9F9F7] flex items-center justify-center px-4" data-testid="not-found-page">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#1A4D2E]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto text-center py-16">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link to="/">
            <img
              src="https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/ibb3s625_UGOLF%20ARAVELL%20-%20ANDORRA%20-%20BLANCO.svg"
              alt="UGOLF ARAVELL Andorra"
              className="h-16 mx-auto brightness-0"
            />
          </Link>
        </motion.div>

        {/* 404 Number */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="relative mb-6"
        >
          <span className="text-[150px] md:text-[200px] font-display font-bold text-[#1A4D2E]/10 leading-none select-none">
            {t.title}
          </span>
          <div className="absolute inset-0 flex items-center justify-center">
            <Flag className="w-16 h-16 md:w-24 md:h-24 text-[#1A4D2E]" />
          </div>
        </motion.div>

        {/* Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4 mb-10"
        >
          <h1 className="font-display text-2xl md:text-3xl font-medium text-[#1A1A1A]">
            {t.subtitle}
          </h1>
          <p className="text-[#666666] max-w-md mx-auto">
            {t.message}
          </p>
        </motion.div>

        {/* Back to home button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-12"
        >
          <Link to="/">
            <Button 
              className="bg-[#1A4D2E] hover:bg-[#153d25] text-white gap-2 px-8 py-6 text-sm"
              data-testid="back-home-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.backHome}
            </Button>
          </Link>
        </motion.div>

        {/* Quick links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-4"
        >
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#888888]">
            {t.suggestions}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link 
              to="/el-camp" 
              className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#333333] hover:border-[#1A4D2E] hover:text-[#1A4D2E] transition-colors"
            >
              <MapPin className="w-4 h-4" />
              {t.elCamp}
            </Link>
            <a 
              href="https://open.teeone.golf/es/aravell" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#333333] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              <Flag className="w-4 h-4" />
              {t.reservar}
            </a>
            <Link 
              to="/contacte" 
              className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#333333] hover:border-[#1A4D2E] hover:text-[#1A4D2E] transition-colors"
            >
              <Phone className="w-4 h-4" />
              {t.contacte}
            </Link>
          </div>
        </motion.div>

        {/* Contact info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-16 pt-8 border-t border-[#E5E5E5]"
        >
          <p className="text-sm text-[#888888]">
            +34 661 19 40 23 · secretaria@aravellgolfclub.com
          </p>
        </motion.div>
      </div>
    </main>
  );
};

export default NotFoundPage;
